/**
 * MO tab — legacy productionOutput_v2.js MO pane. One row per factory + manufacturing order.
 *
 * Exception cards, header filters and search act on the loaded rows client-side and are suppressed
 * when the result is truncated (§4.3). Sort is the one documented exception: `<DataTable>` orders
 * every result, capped or not (DECISIONS.md §30). See design-notes.md.
 */

import { useCallback, useMemo, useState } from 'react';
import type { ColumnDef, GroupColumnDef } from '@tanstack/react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBoxOpen,
  faLayerGroup,
  faMagnifyingGlass,
  faScaleUnbalanced,
  faScroll,
  faStopwatch,
  faTag,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useSearchParams } from 'react-router-dom';

import { DataTable } from '@/components/DataTable';
import {
  decimal,
  defaultFormat,
  groupedColumns,
  integer,
} from '@/components/report/groupedColumns';
import {
  headerFilterOptions,
  multiMatch,
  withHeaderFilters,
} from '@/components/report/headerFilter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import type { ReportQueryResponse, SortSpec } from '@/lib/api';
import { cn } from '@/lib/utils';

// The hide rule lives beside the export layout it has to agree with.
import { isHiddenMoColumn } from './moColumns';
import type { MoExceptionKey } from './moState';
import { SEGMENT, SegmentCard } from './segments';

/** Two decimal places; everything else on this tab is whole figures, percentages via `defaultFormat`. */
const TWO_DECIMAL_FIELDS = new Set([
  'smv',
  'fob_price',
  'fob_price_per_pc',
  'fabric_cost_per_pc',
  'acc_cost_per_pc',
]);

/** Rates never join the totals row: summing SMV or FOB/pc across MOs is meaningless. */
const NO_TOTAL_FIELDS = new Set([
  'pcs_in_set',
  'smv',
  'fob_price',
  'fob_price_per_pc',
  'fabric_cost_per_pc',
  'acc_cost_per_pc',
]);

/** Stage % totals are SUM(cost) / SUM(revenue), never the mean of the percentage column. */
const WEIGHTED_PCT_COLUMNS: readonly { field: string; cost: string; rev: string }[] = [
  { field: 'sfts_fabric_cost_pct', cost: 'sfts_fabric_cost', rev: 'sfts_revenue' },
  { field: 'sfts_acc_cost_pct', cost: 'sfts_acc_cost', rev: 'sfts_revenue' },
  { field: 'sfts_material_cost_pct', cost: 'sfts_material_cost', rev: 'sfts_revenue' },
  { field: 'eins_fabric_cost_pct', cost: 'eins_fabric_cost', rev: 'eins_revenue' },
  { field: 'eins_acc_cost_pct', cost: 'eins_acc_cost', rev: 'eins_revenue' },
  { field: 'eins_material_cost_pct', cost: 'eins_material_cost', rev: 'eins_revenue' },
  { field: 'ship_fabric_cost_pct', cost: 'ship_fabric_cost', rev: 'ship_revenue' },
  { field: 'ship_acc_cost_pct', cost: 'ship_acc_cost', rev: 'ship_revenue' },
  { field: 'ship_material_cost_pct', cost: 'ship_material_cost', rev: 'ship_revenue' },
];

/** Strip `meta.total` from rate leaves so groupedColumns' sum default does not total them. */
function clearRateTotals(
  columns: ColumnDef<Record<string, unknown>>[],
): ColumnDef<Record<string, unknown>>[] {
  return columns.map((col) => {
    if ('columns' in col && Array.isArray(col.columns) && col.columns.length > 0) {
      return {
        ...col,
        columns: clearRateTotals(col.columns),
      } as GroupColumnDef<Record<string, unknown>>;
    }
    const id = col.id;
    if (id === undefined || !NO_TOTAL_FIELDS.has(id) || col.meta?.total !== 'sum') {
      return col;
    }
    const restMeta = { ...col.meta };
    delete restMeta.total;
    return { ...col, meta: restMeta };
  });
}

function sumField(rows: Record<string, unknown>[], field: string): number {
  let sum = 0;
  for (const row of rows) {
    const raw = row[field];
    if (raw === null || raw === undefined || raw === '') continue;
    const n = Number(raw);
    if (!Number.isNaN(n)) sum += n;
  }
  return sum;
}

/** Weighted % total for the rows on screen. */
function weightedPct(
  rows: Record<string, unknown>[],
  costField: string,
  revField: string,
): number | null {
  const cost = sumField(rows, costField);
  const rev = sumField(rows, revField);
  if (rev === 0) return null;
  return (cost / rev) * 100;
}

/** Identity columns with multi-select header filters; options are the values in the loaded result. */
const HEADER_FILTER_FIELDS = [
  'factory',
  'brand',
  'mo',
  'po',
  'working_no',
  'article_no',
  'season',
] as const;

/** Selected values per identity column; empty array = no filter (show all). */
type HeaderFilters = Record<string, string[]>;

const EMPTY_HEADER_FILTERS: HeaderFilters = {
  factory: [],
  brand: [],
  mo: [],
  po: [],
  working_no: [],
  article_no: [],
  season: [],
};

interface MoException {
  key: MoExceptionKey;
  label: string;
  /** Neutral and uncoloured: colouring it would be a status treatment §4.6 has no vocabulary for. */
  icon: IconDefinition;
  test: (r: Record<string, unknown>) => boolean;
}

function isFlagged(value: unknown): boolean {
  return value === true || value === 1 || value === '1';
}

/** SQL supplies these governed flags; React only maps them to the established labels. */
const EXCEPTIONS: MoException[] = [
  {
    key: 'nosmv',
    label: 'Missing SMV',
    icon: faStopwatch,
    test: (r) => isFlagged(r.exception_no_smv),
  },
  {
    key: 'nofob',
    label: 'Missing FOB',
    icon: faTag,
    test: (r) => isFlagged(r.exception_no_fob),
  },
  {
    key: 'nofab',
    label: 'Missing fabric cost only',
    icon: faScroll,
    test: (r) => isFlagged(r.exception_no_fabric),
  },
  {
    key: 'noacc',
    label: 'Missing trims / pack cost only',
    icon: faBoxOpen,
    test: (r) => isFlagged(r.exception_no_accessories),
  },
  {
    key: 'nomat',
    label: 'Missing material cost',
    icon: faLayerGroup,
    test: (r) => isFlagged(r.exception_no_material),
  },
  {
    key: 'matgtfob',
    label: 'Material cost > FOB',
    icon: faScaleUnbalanced,
    test: (r) => isFlagged(r.exception_material_gt_fob),
  },
];

/** True if any cell on the row contains the needle (case-insensitive). */
function rowMatchesGlobal(row: Record<string, unknown>, needle: string): boolean {
  const q = needle.trim().toLowerCase();
  if (q === '') return true;
  for (const value of Object.values(row)) {
    if (value === null || value === undefined || value === '') continue;
    if (String(value).toLowerCase().includes(q)) return true;
  }
  return false;
}

/** Exception cards occupy ReportPage's shared summary slot. */
export function MoExceptionSummary({
  rows,
  isLoading,
  truncated,
  selected,
  onSelectedChange,
}: {
  rows: Record<string, unknown>[];
  isLoading: boolean;
  truncated: boolean;
  selected: MoExceptionKey | null;
  onSelectedChange: (next: MoExceptionKey | null) => void;
}) {
  const exceptionCounts = useMemo(() => {
    const counts: Record<MoExceptionKey, number> = {
      nosmv: 0,
      nofob: 0,
      nofab: 0,
      noacc: 0,
      nomat: 0,
      matgtfob: 0,
    };
    for (const row of rows) {
      for (const exception of EXCEPTIONS) {
        if (exception.test(row)) counts[exception.key]++;
      }
    }
    return counts;
  }, [rows]);

  if (truncated) return null;
  if (isLoading) {
    return (
      <SegmentCard>
        {EXCEPTIONS.map((exception) => (
          <div key={exception.key} className={SEGMENT}>
            <Skeleton className="h-16 w-full" />
          </div>
        ))}
      </SegmentCard>
    );
  }
  if (rows.length === 0) return null;

  return (
    <div className="space-y-1.5">
      <SegmentCard>
        {EXCEPTIONS.map((exception) => {
          const activeCard = selected === exception.key;
          return (
            <button
              key={exception.key}
              type="button"
              aria-pressed={activeCard}
              onClick={() => {
                onSelectedChange(activeCard ? null : exception.key);
              }}
              className={cn(
                SEGMENT,
                'text-left transition-colors hover:bg-accent/50',
                // Inset ring: the card clips at `overflow-hidden`, so an outset one is sliced off.
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                activeCard && 'bg-accent text-accent-foreground hover:bg-accent',
              )}
            >
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <FontAwesomeIcon icon={exception.icon} className="size-3" aria-hidden />
                {exception.label}
              </span>

              <span className="mt-0.5 block">
                <span className="text-2xl leading-tight font-semibold tabular-nums">
                  {integer.format(exceptionCounts[exception.key])}
                </span>
              </span>

              <span className="mt-0.5 text-xs text-muted-foreground">MOs</span>
            </button>
          );
        })}
      </SegmentCard>
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        Click a card to filter the table below; click it again to clear.
        {selected !== null ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => {
              onSelectedChange(null);
            }}
          >
            Clear filter
          </Button>
        ) : null}
      </p>
    </div>
  );
}

export function MoTable({
  data,
  isLoading,
  exceptionFilter,
}: {
  data: ReportQueryResponse<Record<string, unknown>> | undefined;
  isLoading: boolean;
  exceptionFilter: MoExceptionKey | null;
}) {
  const [search, setSearch] = useSearchParams();
  const headerFilters = useMemo(() => {
    const parsed = { ...EMPTY_HEADER_FILTERS };
    for (const field of HEADER_FILTER_FIELDS) {
      parsed[field] = search.getAll(`moFilter.${field}`);
    }
    return parsed;
  }, [search]);
  // Presentation-only search over loaded rows, so it stays local rather than in the URL (§4.4).
  const [globalSearch, setGlobalSearch] = useState('');
  const sort = useMemo<SortSpec[]>(() => {
    const raw = search.get('moSort');
    if (raw === null) return [{ field: 'factory', dir: 'asc' }];
    const separator = raw.lastIndexOf(':');
    const field = raw.slice(0, separator);
    const dir = raw.slice(separator + 1);
    return field !== '' && (dir === 'asc' || dir === 'desc')
      ? [{ field, dir }]
      : [{ field: 'factory', dir: 'asc' }];
  }, [search]);

  const rows = useMemo(() => data?.rows ?? [], [data?.rows]);

  const optionsByField = useMemo(() => {
    return headerFilterOptions(rows, HEADER_FILTER_FIELDS);
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (data?.truncated === true) return rows;
    const ex = exceptionFilter === null ? null : EXCEPTIONS.find((e) => e.key === exceptionFilter);
    return rows.filter((row) => {
      if (ex !== null && ex !== undefined && !ex.test(row)) return false;
      if (!rowMatchesGlobal(row, globalSearch)) return false;
      for (const field of HEADER_FILTER_FIELDS) {
        if (!multiMatch(row[field], headerFilters[field])) return false;
      }
      return true;
    });
  }, [data?.truncated, rows, exceptionFilter, headerFilters, globalSearch]);

  const onFilterChange = useCallback(
    (field: string, values: string[]) => {
      setSearch(
        (current) => {
          const next = new URLSearchParams(current);
          const param = `moFilter.${field}`;
          next.delete(param);
          for (const value of values) next.append(param, value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearch],
  );

  const onSortChange = useCallback(
    (nextSort: SortSpec[]) => {
      setSearch(
        (current) => {
          const next = new URLSearchParams(current);
          const first = nextSort[0];
          if (first === undefined) next.delete('moSort');
          else next.set('moSort', `${first.field}:${first.dir}`);
          return next;
        },
        { replace: true },
      );
    },
    [setSearch],
  );

  const columns = useMemo(() => {
    const base = clearRateTotals(
      groupedColumns<Record<string, unknown>>(data?.columns ?? [], {
        hide: isHiddenMoColumn,
        clientSortable: true,
        format: (value, column) =>
          TWO_DECIMAL_FIELDS.has(column.field) &&
          value !== null &&
          value !== undefined &&
          value !== ''
            ? decimal.format(Number(value))
            : defaultFormat(value, column),
      }),
    );
    if (data?.truncated === true) return base;
    return withHeaderFilters(
      base,
      HEADER_FILTER_FIELDS,
      headerFilters,
      optionsByField,
      onFilterChange,
    );
  }, [data?.columns, data?.truncated, headerFilters, optionsByField, onFilterChange]);

  // Totals only over a complete result (§4.3), summed across the filtered rows on screen.
  const totals = useMemo(() => {
    if (data === undefined || data.truncated || filteredRows.length === 0) return undefined;

    const values: Record<string, React.ReactNode> = {};
    for (const { field, cost, rev } of WEIGHTED_PCT_COLUMNS) {
      const pct = weightedPct(filteredRows, cost, rev);
      values[field] =
        pct === null ? (
          <span className="text-muted-foreground/40">—</span>
        ) : (
          `${decimal.format(pct)}%`
        );
    }

    return {
      labelColumn: 'factory',
      label: 'Total',
      values,
      format: (value: number) => integer.format(Math.round(value)),
    };
  }, [data, filteredRows]);

  return (
    <div className="flex flex-col gap-2">
      {!isLoading && data?.truncated !== true && rows.length > 0 ? (
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex min-w-64 max-w-md flex-1 flex-col gap-1">
            <Label htmlFor="mo-global-search" className="text-xs font-medium text-muted-foreground">
              Search loaded rows
            </Label>
            <div className="relative">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                id="mo-global-search"
                value={globalSearch}
                placeholder="Search all columns…"
                className="h-9 pr-9 pl-8 text-sm"
                onChange={(e) => {
                  setGlobalSearch(e.target.value);
                }}
              />
              {globalSearch !== '' ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label="Clear table search"
                  className="absolute top-1/2 right-1 h-7 w-7 -translate-y-1/2 p-0"
                  onClick={() => {
                    setGlobalSearch('');
                  }}
                >
                  <FontAwesomeIcon icon={faXmark} className="size-3.5" />
                </Button>
              ) : null}
            </div>
          </div>
          <p className="pb-2 text-xs text-muted-foreground tabular-nums">
            {integer.format(filteredRows.length)}
            {filteredRows.length !== rows.length
              ? ` of ${integer.format(rows.length)} rows`
              : ' rows'}
          </p>
        </div>
      ) : null}

      <DataTable
        columns={columns}
        data={filteredRows}
        isLoading={isLoading}
        virtualized
        sort={sort}
        onSortChange={onSortChange}
        totals={totals}
        truncated={data?.truncated === true}
        caption="Production output by manufacturing order"
        emptyMessage={
          rows.length > 0
            ? 'No manufacturing orders match the current search or filters. Clear them to see all rows.'
            : 'No manufacturing orders for these filters. Widen the dates, or clear the factory and brand.'
        }
      />
    </div>
  );
}
